"use strict";
import { CurrencyAmount } from "@uniswap/sdk-core";
import { BNB_LOGO } from "ui/src/assets";
import {
  NetworkLayer,
  RPCType,
  UniverseChainId
} from "features/chains/types";
import { MULTICALL3_ADDRESS } from "constants/bscContracts";
import { ElementName } from "features/telemetry/constants";
import { ONE_MINUTE_MS } from "utilities/src/time/time";
import { bscMainnet, bscTestnetChain } from "./bscWagmiChains";
export const DEFAULT_NATIVE_ADDRESS = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
export const DEFAULT_RETRY_OPTIONS = { n: 10, minWait: 250, maxWait: 1e3 };
export const DEFAULT_MS_BEFORE_WARNING = ONE_MINUTE_MS * 10;
const BSC_MAINNET_RPC_URLS = [
  "https://bsc-dataseed.binance.org",
  "https://bsc-rpc.publicnode.com",
  "https://bsc-dataseed1.binance.org",
  "https://1rpc.io/bnb"
];
const BSC_TESTNET_RPC_URLS = ["https://data-seed-prebsc-1-s1.binance.org:8545"];
export function getChainInfo(chainId) {
  const chainInfo = UNIVERSE_CHAIN_INFO[chainId];
  if (!chainInfo) {
    console.warn(`No chain info found for chainId: ${chainId}`);
    return {
      ...UNIVERSE_CHAIN_INFO[UniverseChainId.Mainnet],
      id: chainId,
      label: `Chain ${chainId}`,
      logo: void 0
    };
  }
  return chainInfo;
}
import {
  USDC_BSC_MAINNET,
  USDC_BSC_TESTNET,
  WBNB_BSC_MAINNET,
  WBNB_BSC_TESTNET
} from "constants/topazTokenList";
export const UNIVERSE_CHAIN_INFO = {
  [UniverseChainId.Mainnet]: {
    ...bscMainnet,
    id: UniverseChainId.Mainnet,
    sdkId: UniverseChainId.Mainnet,
    assetRepoNetworkName: "bnb",
    backendChain: {
      chain: "BNB",
      backendSupported: true,
      isSecondaryChain: false,
      nativeTokenBackendAddress: void 0
    },
    blockPerMainnetEpochForChainId: 1,
    blockWaitMsBeforeWarning: DEFAULT_MS_BEFORE_WARNING,
    bridge: void 0,
    docs: "https://docs.bnbchain.org/",
    elementName: ElementName.ChainEthereum,
    explorer: {
      name: "BscScan",
      url: "https://bscscan.com/",
      apiURL: "https://api.bscscan.com/api"
    },
    helpCenterUrl: void 0,
    infoLink: "https://bscscan.com/",
    infuraPrefix: void 0,
    interfaceName: "bnb",
    label: "BNB Smart Chain",
    logo: BNB_LOGO,
    nativeCurrency: {
      name: "BNB",
      symbol: "BNB",
      decimals: 18,
      address: DEFAULT_NATIVE_ADDRESS,
      explorerLink: "https://bscscan.com/chart/etherprice",
      logo: BNB_LOGO
    },
    networkLayer: NetworkLayer.L1,
    pendingTransactionsRetryOptions: DEFAULT_RETRY_OPTIONS,
    rpcUrls: {
      [RPCType.Default]: {
        http: BSC_MAINNET_RPC_URLS
      },
      [RPCType.Public]: {
        http: BSC_MAINNET_RPC_URLS
      },
      [RPCType.Interface]: {
        http: BSC_MAINNET_RPC_URLS
      }
    },
    spotPriceStablecoinAmount: CurrencyAmount.fromRawAmount(USDC_BSC_MAINNET, 1e10),
    stablecoins: [USDC_BSC_MAINNET],
    statusPage: void 0,
    supportsInterfaceClientSideRouting: true,
    supportsGasEstimates: true,
    supportsV4: false,
    urlParam: "bnb",
    wrappedNativeCurrency: {
      name: "Wrapped BNB",
      symbol: "WBNB",
      decimals: 18,
      address: WBNB_BSC_MAINNET.address
    },
    contracts: {
      multicall3: {
        address: MULTICALL3_ADDRESS
      }
    },
    blockExplorers: {
      default: {
        name: "BscScan",
        url: "https://bscscan.com"
      }
    },
    fees: void 0,
    formatters: void 0,
    serializers: void 0,
    testnet: false
  },
  [UniverseChainId.Sepolia]: {
    ...bscTestnetChain,
    id: UniverseChainId.Sepolia,
    sdkId: UniverseChainId.Sepolia,
    assetRepoNetworkName: void 0,
    backendChain: {
      chain: "BNB_TESTNET",
      backendSupported: true,
      isSecondaryChain: false,
      nativeTokenBackendAddress: void 0
    },
    blockPerMainnetEpochForChainId: 1,
    blockWaitMsBeforeWarning: DEFAULT_MS_BEFORE_WARNING,
    bridge: void 0,
    docs: "https://docs.bnbchain.org/",
    elementName: ElementName.ChainEthereum,
    explorer: {
      name: "BscScan Testnet",
      url: "https://testnet.bscscan.com/",
      apiURL: "https://api-testnet.bscscan.com/api"
    },
    helpCenterUrl: void 0,
    infoLink: "https://testnet.bscscan.com/",
    infuraPrefix: void 0,
    interfaceName: "bnb-testnet",
    label: "BNB Smart Chain Testnet",
    logo: BNB_LOGO,
    nativeCurrency: {
      name: "tBNB",
      symbol: "tBNB",
      decimals: 18,
      address: DEFAULT_NATIVE_ADDRESS,
      explorerLink: "https://testnet.bscscan.com/chart/etherprice",
      logo: BNB_LOGO
    },
    networkLayer: NetworkLayer.L1,
    pendingTransactionsRetryOptions: DEFAULT_RETRY_OPTIONS,
    rpcUrls: {
      [RPCType.Default]: {
        http: BSC_TESTNET_RPC_URLS
      },
      [RPCType.Public]: {
        http: BSC_TESTNET_RPC_URLS
      },
      [RPCType.Interface]: {
        http: BSC_TESTNET_RPC_URLS
      }
    },
    spotPriceStablecoinAmount: CurrencyAmount.fromRawAmount(USDC_BSC_TESTNET, 1e10),
    stablecoins: [USDC_BSC_TESTNET],
    statusPage: void 0,
    supportsInterfaceClientSideRouting: true,
    supportsGasEstimates: true,
    supportsV4: false,
    urlParam: "bnb-testnet",
    wrappedNativeCurrency: {
      name: "Wrapped BNB",
      symbol: "WBNB",
      decimals: 18,
      address: WBNB_BSC_TESTNET.address
    },
    contracts: {
      multicall3: {
        address: MULTICALL3_ADDRESS
      }
    },
    blockExplorers: {
      default: {
        name: "BscScan Testnet",
        url: "https://testnet.bscscan.com"
      }
    },
    fees: void 0,
    formatters: void 0,
    serializers: void 0,
    testnet: true
  }
};
export const GQL_MAINNET_CHAINS = Object.values(UNIVERSE_CHAIN_INFO).filter((chain) => !chain.testnet && !chain.backendChain.isSecondaryChain).map((chain) => chain.backendChain.chain).filter((backendChain) => !!backendChain);
export const GQL_TESTNET_CHAINS = Object.values(UNIVERSE_CHAIN_INFO).filter((chain) => chain.testnet && !chain.backendChain.isSecondaryChain).map((chain) => chain.backendChain.chain).filter((backendChain) => !!backendChain);
export const ALL_GQL_CHAINS = [...GQL_MAINNET_CHAINS, ...GQL_TESTNET_CHAINS];
export function getQuicknodeChainId(_chainId) {
  return "";
}
export function getQuicknodeChainIdPathSuffix(_chainId) {
  return "";
}
export function getQuicknodeEndpointUrl(_chainId) {
  return "";
}
